/** Initiatives: groups of projects with one combined status (Ben Wood's request). */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export type Health = 'green' | 'amber' | 'red' | 'none'

export interface InitiativeProject {
  id: string
  name: string
  status: string
  ragStatus: string
  percentComplete: number
  targetDate: string | null
  ownerName: string
  teamShortLabel: string
}

export interface Initiative {
  id: string
  name: string
  description: string
  ownerUserId: string | null
  ownerName: string | null
  targetDate: string | null
  /** What to show: the override if there is one, else the computed colour. */
  health: Health
  /** Always present, so an override can never hide what the projects say. */
  computedHealth: Health
  healthOverride: Health | null
  healthOverrideReason: string
  healthOverrideBy: string | null
  healthOverrideAt: string | null
  percentComplete: number
  projectCount: number
  liveCount: number
  completeCount: number
  atRiskCount: number
  overdueCount: number
  latestProjectTarget: string | null
  projectsRunPastTarget: boolean
  projects: InitiativeProject[]
  canManage: boolean
  rowVersion: number
  // Detail only.
  nextMilestones?: { projectName: string; title: string; dueDate: string | null; isOverdue: boolean }[]
  waitingOn?: { projectName: string; waitsOn: string; waitsOnStatus: string; insideInitiative: boolean }[]
}

const key = ['initiatives'] as const

function useInvalidate() {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: key })
    void client.invalidateQueries({ queryKey: ['projects'] })
  }
}

export function useInitiatives() {
  return useQuery({
    queryKey: [...key, 'list'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/initiatives')
      return data as unknown as { initiatives: Initiative[]; canManage: boolean }
    },
  })
}

export function useInitiative(id: string | null) {
  return useQuery({
    queryKey: [...key, 'detail', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/initiatives/{initiative_id}', {
        params: { path: { initiative_id: id as string } },
      })
      return data as unknown as Initiative
    },
  })
}

export function useCreateInitiative() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data } = await api.POST('/api/v1/initiatives', { body: body as never })
      return data as unknown as Initiative
    },
    onSuccess: invalidate,
  })
}

export function useUpdateInitiative() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) => {
      const { data } = await api.PATCH('/api/v1/initiatives/{initiative_id}', {
        params: { path: { initiative_id: id } },
        body: body as never,
      })
      return data as unknown as Initiative
    },
    onSuccess: invalidate,
  })
}

export function useDeleteInitiative() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await api.DELETE('/api/v1/initiatives/{initiative_id}', {
        params: { path: { initiative_id: id } },
        body: { reason } as never,
      })
    },
    onSuccess: invalidate,
  })
}

export function useSetMembership() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async ({
      initiativeId,
      projectId,
      add,
      move = false,
    }: {
      initiativeId: string
      projectId: string
      add: boolean
      move?: boolean
    }) => {
      const params = { path: { initiative_id: initiativeId, project_id: projectId } }
      if (add) {
        await api.PUT('/api/v1/initiatives/{initiative_id}/projects/{project_id}', {
          params,
          body: { move } as never,
        })
      } else {
        await api.DELETE('/api/v1/initiatives/{initiative_id}/projects/{project_id}', { params })
      }
    },
    onSuccess: invalidate,
  })
}
